import { useState, useCallback, useEffect, useRef } from 'react';
import { ModalDialog, ButtonBar, FlowForm, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { TextButton } from 't-lib-ui/widgets';
import { getKeyHandler } from 't-lib-ui';
import { usePreferences } from '../connector/preferences';
import DiscreteSlider, { SliderStop } from './DiscreteSlider';
import './PostAppearancePopup.css';

const FONT_SIZE_STOPS: SliderStop[] = Array.from({ length: 11 }, (_, i) => ({ value: 10 + i, label: `${10 + i}` }));
const WIDTH_STOPS: SliderStop[] = Array.from({ length: 6 }, (_, i) => ({ value: 500 + i * 100, label: `${500 + i * 100}` }));

export function usePostAppearance(isMobile?: boolean): { openPostAppearance: () => void; postAppearancePopup: React.ReactNode } {
  const [show, setShow] = useState(false);

  // Toggle: the header's "Aa" button both opens and closes the desktop panel
  // (mousedowns on it are excluded from the panel's outside-click closing).
  const openPostAppearance = useCallback(() => setShow(s => !s), []);

  const postAppearancePopup = (
    <PostAppearancePopup show={show} isMobile={isMobile} onClose={() => setShow(false)} />
  );

  return { openPostAppearance, postAppearancePopup };
}

interface PostAppearancePopupProps {
  show: boolean;
  isMobile?: boolean;
  onClose: () => void;
}

// All controls apply immediately so the post column acts as a live preview;
// usePreferences debounces the server save, so dragging through intermediate
// values costs nothing.
//
// Desktop renders a non-modal panel anchored below the header's "Aa" button —
// a ModalDialog's blurred backdrop would hide the very effect being previewed.
// Mobile keeps the full-screen sheet (there's no visible post column behind it
// anyway, and the panel has no good anchor in the mobile header).
const PostAppearancePopup: React.FC<PostAppearancePopupProps> = ({ show, isMobile, onClose }) => {
  const [preferences, updatePreferences] = usePreferences();
  const postFont = preferences.postFont ?? 'serif';
  const panelRef = useRef<HTMLDivElement>(null);

  // Desktop panel: Esc closes (above the layout's Esc-collapses-sidebar handler),
  // clicking outside closes. Mousedowns on the "Aa" trigger are ignored so its
  // click toggles the panel instead of close-then-reopen.
  useEffect(() => {
    if (!show || isMobile) return;
    registerKeyHandler('postAppearancePanel', {
      'Escape': () => { onClose(); return true; },
    }, false, 30);
    const onMouseDown = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;
      if (target.closest('.postAppearanceTrigger')) return;
      if (panelRef.current && !panelRef.current.contains(target)) onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      unregisterKeyHandler('postAppearancePanel');
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [show, isMobile, onClose]);

  // Keyboard users (visibleFocus on the surrounding .postView) get focus moved
  // into the panel on open, mirroring the header menus' auto-focus behavior.
  useEffect(() => {
    if (!show || isMobile) return;
    const panel = panelRef.current;
    if (!panel || !panel.closest('.visibleFocus')) return;
    const first = panel.querySelector('.flowFocussable:not(:disabled)') as HTMLElement | null;
    if (first) requestAnimationFrame(() => first.focus());
  }, [show, isMobile]);

  // Restore the defaults: serif body, 16px, 700px wide.
  const resetDefaults = () => updatePreferences({ postFont: 'serif', postFontSize: 16, postWidth: 700 });

  const controls = (
    <FlowForm>
      <div className="postAppearanceLabel">Body font</div>
      <div className="postFontGroup">
        <button
          className={`postFontOption fontSerif flowFocussable${postFont == 'serif' ? ' active' : ''}`}
          onClick={() => updatePreferences({ postFont: 'serif' })}
          onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'right',
            onAction: () => updatePreferences({ postFont: 'serif' }) })}
        >
          Serif
        </button>
        <button
          className={`postFontOption fontSansSerif flowFocussable sidewayFocus${postFont == 'sans-serif' ? ' active' : ''}`}
          onClick={() => updatePreferences({ postFont: 'sans-serif' })}
          onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'left',
            onAction: () => updatePreferences({ postFont: 'sans-serif' }) })}
        >
          Sans-Serif
        </button>
      </div>
      <DiscreteSlider label="Font size" stops={FONT_SIZE_STOPS} value={preferences.postFontSize ?? 16}
        onChange={v => updatePreferences({ postFontSize: v })} />
      {!isMobile && (
        <DiscreteSlider label="Post width" stops={WIDTH_STOPS} value={preferences.postWidth ?? 700}
          onChange={v => updatePreferences({ postWidth: v })} />
      )}
      <ButtonBar className="full-margin-top" align="right">
        <TextButton type="minor" onClick={resetDefaults} label="Reset to defaults" />
        <TextButton type="major" focussable="sideway" onClick={onClose} label="Done" />
      </ButtonBar>
    </FlowForm>
  );

  if (isMobile) {
    return (
      <ModalDialog
        show={show}
        title="Text appearance"
        closeButton="close"
        onClose={onClose}
        className="postAppearancePopup"
      >
        {controls}
      </ModalDialog>
    );
  }

  if (!show) return null;
  return (
    <div className="postAppearancePanel postAppearancePopup flowFormModal" ref={panelRef}>
      <div className="postAppearancePanelHeader">
        <span className="postAppearancePanelTitle">Text appearance</span>
        <button className="postAppearancePanelClose flowFocussable sidewayFocus" aria-label="Close" onClick={onClose}>&times;</button>
      </div>
      {controls}
    </div>
  );
};

export default PostAppearancePopup;
